import { defineStore } from "pinia";
import md5 from "js-md5";
import { useSystemStore } from "./system";
import customDialog from "src/components/dialogs/CustomDialogComponent.vue";
import { aesDecrypt, aesEncrypt } from "./lib/aes";
import {
  GET_COFRE_CHAVE,
  GET_TOKEN,
  LOGIN_API,
  USUARIO_CHANGE_PWD,
} from "./lib/apis";
import { generateKey, hex2String, stringToArray } from "./lib/utils";
import { Dialog } from "quasar";
import { useScheduleStore } from "./schedule";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: {
      authenticated: false,
      administrator: false,
      login: "",
      name: "",
      password: "",
      ha1: "",
      nC: 0,
      Reconect: 0,
      cNonce: "",
      perfil: "",
    },
  }),
  getters: {
    acessoMorto(state) {
      return state.user?.acessomorto;
    },
    email(state) {
      return state.user?.email;
    },
    hasAccessDashboard(state) {
      if (!state.user.authenticated) return false;
      return state.user?.acessodashboard ? true : false;
    },
    hasAccessMaintenanceDash(state) {
      if (!state.user.authenticated) return false;
      if (typeof state.user.sissuportaMDash === "undefined") return true;
      return state.user?.sissuportaMDash;
    },
    hasAccessProcess(state) {
      if (!state.user.authenticated) return false;
      return state.user?.sissuportaconsulta && state.user?.acessoconsulta;
    },
    hasAccessSchedule(state) {
      if (!state.user.authenticated) return false;
      return state.user?.sissuportaconsulta && state.user?.acessoagenda;
    },
    hasAccessSystem(state) {
      if (!state.user.authenticated) return false;
      return (
        this.hasAccessDashboard ||
        this.hasAccessProcess ||
        this.hasAccessSchedule
      );
    },
    hasFichaCompleta(state) {
      return state.user.fichacompleta ? true : false;
    },
    hasPanel(state) {
      return (state?.user?.possuiColecao ?? 0) != 0;
    },
    idUsuario(state) {
      return state.user?.id_usuario;
    },
    isAdministrator(state) {
      return state.user.administrator === 1;
    },
    isLoggedIn(state) {
      return state.user?.authenticated ?? false;
    },
    importarkitPadrao(state) {
      return state.user?.importarkitPadrao;
    },
    janelainicio(state) {
      if (state.user?.janelainicio == 1) return "/schedule";
      if (state.user?.janelainicio == 2) return "/process";
      return "/dashboard";
    },
    pathAccess(state) {
      return state.user?.acesso;
    },
    perfil(state) {
      return state.user?.perfil;
    },
    pessoaAgenda(state) {
      return state.user?.pessoa_agenda;
    },
    senha(state) {
      return state.user?.password;
    },
    tipo(state) {
      return state.user?.tipo;
    },
    urlOauth2(state) {
      return state.user?.url_oauth2;
    },
    userLogin(state) {
      return state.user?.login;
    },
    username(state) {
      return state.user?.name;
    },
  },
  actions: {
    clearAuth() {
      this.user = {
        authenticated: false,
        administrator: false,
        login: "",
        name: "",
        password: "",
        ha1: "",
        nC: 0,
        Reconect: 0,
        cNonce: "",
        perfil: "",
      };
    },
    login(credentials) {
      return new Promise((resolve, reject) => {
        // importa as outras stores
        const systemStore = useSystemStore();
        // retorna erro caso não tenha recebido parâmetros corretos
        if (!credentials || !credentials.login || !credentials.pwd) {
          reject({
            sysMsg: "missing-params",
            params: "stores/auth.js -> login()",
          });
          return;
        }

        // seta o usuário da store se não estiver reconectando
        if (!systemStore.isReconnecting)
          this.setUserValues({
            authenticated: false,
            administrador: false,
            ad: false,
            login: credentials.login,
            password: credentials.pwd,
            nC: 0,
          });

        // primeiramente, tenta obter o token
        systemStore
          .post({ url: GET_TOKEN, responseType: "text" })
          // conseguiu obter o token
          .then((resp) => {
            // verifica se chegou o token
            let token = resp?.data?.token;
            if (!token || token.length < 32) {
              reject({
                sysMsg: "wrong-response",
                params: GET_TOKEN + " (token inválido)",
              });
              return;
            }

            // verifica se é um usuário AD
            if (resp?.data?.ad !== undefined) this.setUserValues({ ad: true });
            this.setUserValues({ token: token });

            // prepara a chamada do login
            let ret = {
              login: credentials.login,
            };
            if (this.user.ad) {
              let realm = hex2String(data.ad.realm);
              let nonce = hex2String(data.ad.nonce);
              let a2 = hex2String(data.ad.a2);
              let nc = "00000001";
              let qop = "auth";
              let cnonce = "02432B22";
              let array = md5.array(
                credentials.login + ":" + realm + ":" + credentials.pwd
              );
              let a1 = stringToArray(array, ":" + nonce + ":" + cnonce);
              let s =
                md5(a1) +
                ":" +
                nonce +
                ":" +
                nc +
                ":" +
                cnonce +
                ":" +
                qop +
                ":" +
                md5(a2);
              let response = md5(s);
              ret.response = response;
              ret.cnonce = cnonce;
            } else {
              let pwd = credentials.pwd.toUpperCase().substr(0, 15);
              let keyS = generateKey(pwd);
              this.setUserValues({
                ha1: aesEncrypt(pwd, keyS, false).toUpperCase(),
              });
            }
            // faz a chamada de login
            systemStore
              .post({ url: LOGIN_API, data: ret, responseType: "text" })
              // login bem sucedido
              .then((resp) => {
                // caso seja ad, tem que pegar o nova senha para as novas solicitações
                if (this.user.ad) {
                  let pwd = resp?.headers?.dashboard;
                  let keyS = generateKey(pwd);
                  this.setUserValues({
                    ha1: aesEncrypt(pwd, keyS, false).toUpperCase(),
                  });
                }

                // preenche os dados do usuário com as informações do servidor
                let data = resp.data;
                this.setUserValues({
                  authenticated: true,
                  id_usuario: data.id_usuario,
                  oauth2_google: data.oauth2_google,
                  administrator: data.administrator,
                  pessoa_agenda: data.pessoa_agenda,
                  tipo: data.tipo,
                  email: data.email,
                  perfil: data.perfil,
                  login: credentials.login,
                  name: data.name,
                  acessomorto: data.acessomorto,
                  janelainicio: data.janelainicio,
                  id_dashboard_provider: data.id_dashboard_provider,
                  versao_banco: data.versao_banco,
                  acesso: data.acesso,
                  fichacompleta: data.fichacompleta,
                  acessoconsulta: data.acessoconsulta,
                  acessoagenda: data.acessoagenda,
                  acessodashboard: data.acessodashboard,
                  sissuportaconsulta: data.sissuportaconsulta,
                  sissuportaMDash: data.sissuportaMDash,
                  possuiColecao: data.possuiColecao,
                  nReconect: 0,
                  url_oauth2: data.url_oauth2,
                });

                // verifica se o usuário tem acesso ao sistema
                if (!this.hasAccessSystem) {
                  reject({ sysMsg: "no-system-access" });
                  return;
                }

                // carrega os parâmetros do sistema do usuário, caso não seja uma chamada de reconexão
                if (!systemStore.isReconnecting) {
                  systemStore
                    .loadParams()
                    // carregou parâmetros
                    .then(() => {
                      resolve(resp);
                    })
                    // carregar parâmetros falhou
                    .catch((eParams) => {
                      reject(eParams);
                      return;
                    });
                } else {
                  resolve(resp);
                }
              })
              //login falhou
              .catch((e) => {
                console.error(e);
                reject(e);
                return;
              });
          })
          // token falhou
          .catch((e) => {
            reject(e);
            return;
          });
      });
    },
    logout(router) {
      // importa store do sistema
      const systemStore = useSystemStore();
      systemStore.clearStore();
      if (router.push) router.push({ path: "/", replace: true });
    },
    setUserAuthenticated(authenticated = false) {
      this.user.authenticated = authenticated;
    },
    setUserPossuiColecao(params) {
      this.user.possuiColecao = params;
    },
    setUserValues(userObject) {
      for (let key in userObject) {
        this.user[key] = userObject[key];
      }
    },
    trocarSenha() {
      return new Promise((resolve, reject) => {
        // importa as outras stores
        const scheduleStore = useScheduleStore();
        const systemStore = useSystemStore();

        // verifica se o usuário é AD
        if (this.user.ad) {
          reject();
          return;
        }

        if (
          systemStore.parametros.sisRg == "RG 00262" &&
          this.userLogin.toUpperCase() == "DEMO"
        ) {
          systemStore.showMessage({ sysMsg: "access-denied" });
          reject();
          return;
        }

        // altera o estado da store pra 'alterando senha', para permitir as chamadas
        systemStore.setIsChangingPwd(true);
        systemStore.setStatus("ok");
        Dialog.create({
          component: customDialog,
          componentProps: {
            title: "Alterar senha",
            formDef: {
              senhaAntiga: {
                name: "senhaAntiga",
                type: "password",
                label: "Senha antiga",
              },
              novaSenha: {
                name: "novaSenha",
                type: "password",
                label: "Nova senha",
                rules: [
                  (val) =>
                    val.length > 3 ||
                    "Sua senha deve conter pelo menos 4 caracteres.",
                ],
              },
              confSenha: {
                name: "confSenha",
                type: "password",
                label: "Confirmar senha",
              },
            },
            ok: true,
            cancel: true,
          },
        }).onOk((form, close) => {
          // desencripta senha da session
          let decryptedPwd = this?.user?.password;

          // verifica se o usuário preencheu todos os campos
          if (!form.novaSenha || !form.confSenha) {
            systemStore.showMessage({
              sysMsg: "change-pwd-required-fields",
            });
            reject();
            return;
          }

          // verifica se a senha antiga confere
          if (form.senhaAntiga !== decryptedPwd) {
            systemStore.showMessage({
              sysMsg: "change-pwd-old-pwd-mismatch",
            });
            reject();
            return;
          }

          // verifica se as novas senhas conferem
          if (form.novaSenha != form.confSenha) {
            systemStore.showMessage({
              sysMsg: "change-pwd-different-pwd",
            });
            reject();
            return;
          }

          // verifica se a nova senha é diferente da antiga
          if (
            decryptedPwd === form.novaSenha ||
            decryptedPwd === form.confSenha
          ) {
            systemStore.showMessage({ sysMsg: "change-pwd-same-pwd" });
            reject();
            return;
          }

          // encriptografa a nova senha
          let pwd = decryptedPwd.toUpperCase().substr(0, 15);
          let keyS = generateKey(pwd);
          keyS = aesEncrypt(pwd, keyS, false).toUpperCase();
          this.user.password = decryptedPwd;
          let encryptedNewPassword = aesEncrypt(
            form.novaSenha,
            keyS,
            false
          ).toUpperCase();

          // prepara a requisição para acessar o cofre. É necessário pois após mudar a senha, temos que re-encriptografar todo o cofre
          let reqCofre = {
            id_usuario: this.idUsuario,
          };

          scheduleStore
            .getCofreChave(reqCofre)
            .then((resp) => {
              if (resp) {
                try {
                  let arrayOfValues = [];
                  for (let item of resp) {
                    if (!item.valor || !item.chave) continue;
                    let pwdOldFormatted = this.user.password
                      .toUpperCase()
                      .substr(0, 15);
                    let pwdNewFormatted = form.novaSenha
                      .toUpperCase()
                      .substr(0, 15);
                    let keyOld = md5(
                      pwdOldFormatted + "4E6218AF20DD2225BE38A8B73A523F09"
                    ).toUpperCase();
                    let keyNew = md5(
                      pwdNewFormatted + "4E6218AF20DD2225BE38A8B73A523F09"
                    ).toUpperCase();

                    let decryptedItem = aesDecrypt(
                      hex2String(item.valor),
                      keyOld,
                      true
                    );
                    arrayOfValues.push({
                      chave: item.chave,
                      valor: aesEncrypt(decryptedItem, keyNew).toUpperCase(),
                    });
                  }
                  let cofre =
                    arrayOfValues.length > 0
                      ? {
                          id_usuario: this.idUsuario,
                          valores: arrayOfValues,
                        }
                      : "";
                  let reqTrocaSenha = {
                    senha: encryptedNewPassword,
                    cofre: cofre,
                  };

                  // faz a chamada que altera a senha
                  systemStore
                    .post({
                      url: USUARIO_CHANGE_PWD,
                      data: { ...reqTrocaSenha },
                      responseType: "text",
                    })
                    // caso a chamada seja bem sucedida
                    .then((resp) => {
                      systemStore.showMessage({
                        sysMsg: "change-pwd-success",
                      });
                      this.user.password = form.novaSenha;
                      this.user.authenticated = true;
                      pwd = this.user.password.toUpperCase().substr(0, 15);
                      keyS = generateKey(pwd);
                      this.user.ha1 = aesEncrypt(
                        pwd,
                        keyS,
                        false
                      ).toUpperCase();
                      resolve(resp);
                      close();
                    })
                    .catch((e) => {
                      reject(e);
                    });
                } catch (err) {
                  reject({
                    sysMsg: "invalid-response",
                    params: GET_COFRE_CHAVE,
                  });
                }
              }
            })
            .catch((e) => systemStore.showMessage(e))
            .finally(() => {
              systemStore.setIsChangingPwd(false);
            });
        });
      });
    },
  },
});
